// src/context/AuthContext.jsx
import { createContext, useContext, useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Initialize with cached user if available (instant)
    const auth = getAuth();
    return !!auth.currentUser;
  });
  
  const [isLoading, setIsLoading] = useState(() => {
    // If we already have a cached user, skip loading
    const auth = getAuth();
    return !auth.currentUser;
  });
  
  const [user, setUser] = useState(() => {
    const auth = getAuth();
    return auth.currentUser;
  });

  useEffect(() => {
    const auth = getAuth();
    
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setIsAuthenticated(!!firebaseUser);
      setIsLoading(false);
      
      // Also sync developer auth for backward compatibility
      const savedAuth = localStorage.getItem('developerAuthenticated');
      if (savedAuth === 'true') {
        setIsAuthenticated(true);
      }
    });

    return () => unsubscribe();
  }, []);

  const login = () => {
    setIsAuthenticated(true);
    localStorage.setItem('developerAuthenticated', 'true');
  };

  const logout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('developerAuthenticated');
  };

  const value = {
    isAuthenticated,
    isLoading,
    user,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;